import { useState } from 'react';
import PageMeta from '@/components/feature/PageMeta';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials, faqs } from '@/mocks/novels';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9sv3atlbrt1qcgajan0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 嫂子的诱惑',
    description: '了解「嫂子的诱惑」编辑部——专注都市禁忌情感小说的内容推荐平台，汇聚热门网络小说、影视改编作品与情感解析内容。',
    url: 'https://cbdti.cn/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '嫂子的诱惑',
      url: 'https://cbdti.cn/',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <PageMeta
      title="关于我们 - 嫂子的诱惑 | 品牌介绍与联系方式"
      description="了解「嫂子的诱惑」编辑部——专注都市禁忌情感小说的内容推荐平台。品牌故事、读者口碑评价与联系信息。"
      keywords="嫂子的诱惑,关于我们,联系方式,品牌介绍,读者口碑"
      canonicalUrl="https://cbdti.cn/about"
      ogTitle="关于我们 - 嫂子的诱惑"
      ogDescription="了解「嫂子的诱惑」编辑部——专注都市禁忌情感小说的内容推荐平台。"
      ogType="website"
      ogUrl="https://cbdti.cn/about"
      twitterCard="summary_large_image"
      jsonLd={[aboutSchema, faqSchema]}
    >
      <main className="min-h-screen bg-background-50">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-8 mb-16">
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">
                关于我们
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                用心推荐每一部值得阅读的好作品
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-heading text-2xl text-foreground-900 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    「嫂子的诱惑」诞生于一群热爱网络文学的朋友之手。我们来自不同的行业——有文学编辑、有互联网从业者、有心理学研究者——但我们有一个共同的爱好：在深夜的灯光下，沉浸在精彩的都市情感故事中。
                  </p>
                  <p>
                    我们发现，虽然市面上的小说推荐平台很多，但大多数推荐机制过于商业化——要么是纯粹的流量热榜，要么是算法推荐的同质化内容。真正有价值的、在叙事结构和情感表达上有独特魅力的作品，往往被埋没在信息的洪流中。
                  </p>
                  <p>
                    于是，在2024年，「嫂子的诱惑」应运而生。我们不追求做最大最全的文学平台，而是专注于一个细分的领域——都市禁忌情感小说——深耕细作。每一篇推荐、每一段导读、每一次情感解析，都凝结着编辑团队的心血与热爱。
                  </p>
                  <p>
                    我们的名字取自平台最受欢迎也最具代表性的作品系列，它象征着我们在情感叙事上的核心关注点：那些跨越道德边界、挑战社会规范、深入人性深处的情感故事。这些故事之所以动人，正是因为它们揭示了人性中最真实、最复杂的一面。
                  </p>
                </div>
              </div>
              <div className="bg-background-100 rounded-lg border border-background-200/70 p-8">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <p className="text-3xl font-heading text-accent-500">3,000+</p>
                    <p className="text-xs text-foreground-500 mt-1">深度推荐文章</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-heading text-accent-500">50万+</p>
                    <p className="text-xs text-foreground-500 mt-1">忠实读者</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-heading text-accent-500">12人</p>
                    <p className="text-xs text-foreground-500 mt-1">专业编辑团队</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-heading text-accent-500">98.5%</p>
                    <p className="text-xs text-foreground-500 mt-1">读者好评率</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                读者口碑
              </h2>
              <p className="text-sm text-foreground-500">来自读者们的真实声音</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-100 rounded-lg border border-background-200/70 p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.date}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: t.rating }, (_, i) => (
                        <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            <div>
              <h2 className="font-heading text-2xl text-foreground-900 mb-6">联系我们</h2>
              {formStatus === 'success' ? (
                <div className="bg-accent-100/80 border border-accent-200 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-200 text-accent-700">
                    <i className="ri-check-line text-xl"></i>
                  </div>
                  <p className="text-foreground-800 font-medium">感谢您的留言！</p>
                  <p className="text-sm text-foreground-600 mt-1">我们会尽快回复您的消息。</p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="bg-background-100 rounded-lg border border-background-200/70 p-6 space-y-4"
                  data-readdy-form
                >
                  <div className="honeypot-wrapper">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300/60 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300/60 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      name="subject"
                      required
                      className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300/60 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-3 py-2 text-sm bg-background-50 border border-background-300/60 rounded-md text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none transition-colors"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  {formStatus === 'error' && formError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{formError}</p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'loading' ? '提交中...' : '发送留言'}
                  </button>
                </form>
              )}
            </div>

            <div>
              <h2 className="font-heading text-2xl text-foreground-900 mb-6">联系信息</h2>
              <div className="bg-background-100 rounded-lg border border-background-200/70 p-6 space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-0.5">电子邮箱</p>
                    <p className="text-sm text-foreground-600">contact@saozideyouhuo.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                    <i className="ri-wechat-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-0.5">微信公众号</p>
                    <p className="text-sm text-foreground-600">嫂子的诱惑编辑部</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                    <i className="ri-weibo-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-0.5">新浪微博</p>
                    <p className="text-sm text-foreground-600">@嫂子的诱惑官方微博</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800 mb-0.5">工作时间</p>
                    <p className="text-sm text-foreground-600">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm text-foreground-500">关于平台的常见疑问解答</p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">
                      {faq.question}
                    </span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-500 transition-transform duration-200 flex-shrink-0 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>
    </PageMeta>
  );
}